/**
 * Honorar-Vorlagen: eigene DOCX-Vorlagen (mit Platzhaltern) hochladen/verwalten.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Upload, Trash2, FileText, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { honorarDocsAPI } from '../../lib/api';
import { OfficeEditButton } from '../../components/office/OfficeEditButton';
import { OfficeVersionsButton } from '../../components/office/OfficeVersionsDialog';

export const HonorarTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [docTypes, setDocTypes] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [docType, setDocType] = useState('rahmenvertrag');
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        honorarDocsAPI.listTemplates(),
        honorarDocsAPI.getPlaceholders(),
      ]);
      setTemplates(tRes.data);
      setDocTypes(pRes.data.doc_types || {});
      setCatalog(pRes.data.catalog || []);
    } catch {
      toast.error('Vorlagen konnten nicht geladen werden');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Bitte eine .docx-Datei wählen'); return; }
    if (!name.trim()) { toast.error('Bitte einen Namen vergeben'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name.trim());
      fd.append('doc_type', docType);
      await honorarDocsAPI.uploadTemplate(fd);
      toast.success('Vorlage hochgeladen');
      setName('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally { setUploading(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    try { await honorarDocsAPI.deleteTemplate(id); toast.success('Vorlage gelöscht'); load(); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const copyPlaceholder = (key) => {
    navigator.clipboard?.writeText(`{{ ${key} }}`);
    toast.success(`{{ ${key} }} kopiert`);
  };

  return (
    <div className="space-y-6" data-testid="honorar-templates-tab">
      <div className="rounded-lg border border-dashed p-4 space-y-3">
        <p className="font-medium flex items-center gap-2"><Upload className="h-4 w-4" /> Neue DOCX-Vorlage hochladen</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Typ</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="honorar-tpl-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(docTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Rahmenvertrag 2026" data-testid="honorar-tpl-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Datei (.docx)</Label>
            <Input type="file" accept=".docx" ref={fileRef} data-testid="honorar-tpl-file" />
          </div>
        </div>
        <Button onClick={onUpload} disabled={uploading} className="rounded-full" data-testid="honorar-tpl-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Hochladen
        </Button>
      </div>

      <div>
        <p className="font-medium mb-2">Vorhandene Vorlagen</p>
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Vorlagen hochgeladen.</p>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30" data-testid={`honorar-tpl-${t.id}`}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{t.name}</p>
                  <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-xs">{t.doc_type_label}</Badge>
                    <span>{(t.placeholders || []).length} Platzhalter</span>
                  </div>
                </div>
                <OfficeEditButton source="honorar_template" id={t.id} fileType="docx" onReturn={load} />
                <OfficeVersionsButton source="honorar_template" id={t.id} fileType="docx" onChanged={load} />
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => onDelete(t.id)} data-testid={`honorar-tpl-delete-${t.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border p-4">
        <p className="font-medium mb-2">Verfügbare Platzhalter</p>
        <p className="text-xs text-muted-foreground mb-3">Füge diese als <code>{'{{ platzhalter }}'}</code> in deine DOCX-Datei ein. Klick kopiert den Platzhalter.</p>
        <div className="space-y-3">
          {catalog.map((g) => (
            <div key={g.group}>
              <p className="text-xs font-semibold text-muted-foreground mb-1">{g.group}</p>
              <div className="flex flex-wrap gap-1.5">
                {g.keys.map((k) => (
                  <button key={k} type="button" onClick={() => copyPlaceholder(k)}
                    className="inline-flex items-center gap-1 text-xs font-mono bg-muted hover:bg-muted/70 rounded px-2 py-1 transition-colors"
                    data-testid={`placeholder-${k}`}>
                    {`{{ ${k} }}`} <Copy className="h-3 w-3 opacity-50" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
